/**
 * Self-test suite for KeyAuditor / KeyAuditStore.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/KeyAuditSelfTest.swift`.
 * Auto-discovered by `selftest/index.ts` via `import.meta.glob`.
 *
 * Each test case opens its own IndexedDB database with a unique name derived
 * from `crypto.randomUUID()` and cleans it up in a `finally` block. Tests are
 * therefore fully isolated — parallel runs and HMR reloads cannot share state.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import { KeyAuditorIDB } from "./KeyAuditor";
import type { KeyObservationRecord, KeyObservationSource } from "./KeyAuditor";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

/** 32-byte all-0xAA key — "keyA" in iOS fixture parlance. */
const KEY_A = new Uint8Array(32).fill(0xaa);

/** 32-byte all-0xBB key — "keyB". */
const KEY_B = new Uint8Array(32).fill(0xbb);

/** 32-byte all-0xCC key — a third distinct key for multi-observation tests. */
const KEY_C = new Uint8Array(32).fill(0xcc);

function makeObs(
  userId: string,
  key: Uint8Array,
  opts?: { observedAt?: number; source?: KeyObservationSource; rootKeyFingerprint?: string },
): KeyObservationRecord {
  return {
    userId,
    identityPubKey: key,
    observedAt: opts?.observedAt ?? Date.now(),
    source: opts?.source ?? "preKeyBundle",
    ...(opts?.rootKeyFingerprint !== undefined
      ? { rootKeyFingerprint: opts.rootKeyFingerprint }
      : {}),
  };
}

/** Open a fresh IDB with a unique name; caller must close + delete it. */
async function freshDB(): Promise<{ auditor: KeyAuditorIDB; dbName: string }> {
  const dbName = `kaether-keyaudit-test-${crypto.randomUUID()}`;
  const auditor = await KeyAuditorIDB.open(dbName);
  return { auditor, dbName };
}

/** Convenience: close + delete. Swallows errors so teardown never masks the real failure. */
async function cleanup(auditor: KeyAuditorIDB, dbName: string): Promise<void> {
  try {
    auditor.close();
    await KeyAuditorIDB.deleteDatabase(dbName);
  } catch {
    // intentionally swallowed
  }
}

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

/**
 * TC-1 — First observation.
 *
 * After recording a single observation:
 *   - `record()` returns `'first-observation'`
 *   - `latest()` returns that same record
 *   - A subsequent `compareAgainstLatest()` with a *different* key returns
 *     `'changed'`, not `'first-observation'`, confirming the verdict is only
 *     `'first-observation'` on the very first record for a user.
 */
async function testFirstObservation(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    const verdict = await auditor.record(makeObs("alice", KEY_A));
    if (verdict !== "first-observation") {
      return fail("first observation", `record() returned '${verdict}', want 'first-observation'`);
    }

    const latest = await auditor.latest("alice");
    if (latest === null) {
      return fail("first observation", "latest() returned null after first record");
    }
    if (latest.userId !== "alice") {
      return fail("first observation", `latest().userId = '${latest.userId}', want 'alice'`);
    }

    // After the first record exists, a subsequent compareAgainstLatest with a
    // different key must NOT return 'first-observation'.
    const cmp = await auditor.compareAgainstLatest(makeObs("alice", KEY_B));
    if (cmp !== "changed") {
      return fail(
        "first observation",
        `compareAgainstLatest with different key after first record returned '${cmp}', want 'changed'`,
      );
    }

    return pass("first observation");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-2 — Unchanged.
 *
 * Recording the same key a second time returns `'unchanged'`.
 */
async function testUnchanged(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    await auditor.record(makeObs("alice", KEY_A));
    const verdict = await auditor.record(makeObs("alice", KEY_A));

    if (verdict !== "unchanged") {
      return fail("unchanged", `record() returned '${verdict}', want 'unchanged'`);
    }

    // compareAgainstLatest must agree without mutating.
    const cmp = await auditor.compareAgainstLatest(makeObs("alice", KEY_A));
    if (cmp !== "unchanged") {
      return fail("unchanged", `compareAgainstLatest returned '${cmp}', want 'unchanged'`);
    }

    return pass("unchanged");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-3 — Changed.
 *
 * Recording a different key after an initial observation returns `'changed'`.
 */
async function testChanged(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    await auditor.record(makeObs("alice", KEY_A));
    const verdict = await auditor.record(makeObs("alice", KEY_B));

    if (verdict !== "changed") {
      return fail("changed", `record() returned '${verdict}', want 'changed'`);
    }

    // latest() should now point to KEY_B.
    const latest = await auditor.latest("alice");
    if (latest === null) {
      return fail("changed", "latest() returned null after two records");
    }
    if (latest.identityPubKey[0] !== 0xbb) {
      return fail(
        "changed",
        `latest().identityPubKey[0] = 0x${latest.identityPubKey[0].toString(16)}, want 0xbb`,
      );
    }

    return pass("changed");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-4 — History order.
 *
 * Recording 5 observations and requesting `history(userId, 3)` returns the 3
 * most-recently inserted in most-recent-first order.
 */
async function testHistoryOrder(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    const keys = [KEY_A, KEY_B, KEY_C, KEY_A, KEY_B]; // indices 0..4
    for (let i = 0; i < keys.length; i++) {
      await auditor.record(makeObs("alice", keys[i], { observedAt: 1_000 + i * 1_000 }));
    }

    const history = await auditor.history("alice", 3);

    if (history.length !== 3) {
      return fail("history order", `history.length = ${history.length}, want 3`);
    }

    // Most-recent first: insertion index 4 (KEY_B), 3 (KEY_A), 2 (KEY_C).
    const expectedFirstBytes = [0xbb, 0xaa, 0xcc];
    for (let i = 0; i < 3; i++) {
      const got = history[i].identityPubKey[0];
      const want = expectedFirstBytes[i];
      if (got !== want) {
        return fail(
          "history order",
          `history[${i}].identityPubKey[0] = 0x${got.toString(16)}, want 0x${want.toString(16)}`,
        );
      }
    }

    return pass("history order");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-5 — Append-only invariant.
 *
 * There is no public `delete` method on the `KeyAuditStore` interface.
 * Re-recording the same observation does NOT compact the log — both rows
 * remain, so `history(userId, 0)` returns 2 entries.
 */
async function testAppendOnly(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    // Verify no delete method exists on the public interface.
    // TypeScript types enforce this at compile time; at runtime we do a duck-
    // type check so the assertion survives JS minification.
    if ("delete" in auditor || "clear" in auditor || "reset" in auditor) {
      return fail(
        "append-only",
        "KeyAuditStore exposes a delete/clear/reset method — breaks append-only invariant",
      );
    }

    await auditor.record(makeObs("alice", KEY_A, { observedAt: 1_000 }));
    await auditor.record(makeObs("alice", KEY_A, { observedAt: 2_000 }));

    const all = await auditor.history("alice", 0);
    if (all.length !== 2) {
      return fail(
        "append-only",
        `history after two identical records has ${all.length} rows, want 2 (no compaction)`,
      );
    }

    return pass("append-only");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-6 — Per-userId isolation.
 *
 * A record for user A must not appear in `history(B)`.
 */
async function testPerUserIsolation(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    await auditor.record(makeObs("alice", KEY_A));

    const bobHistory = await auditor.history("bob", 0);
    if (bobHistory.length !== 0) {
      return fail(
        "per-userId isolation",
        `bob's history has ${bobHistory.length} entries after recording for alice only`,
      );
    }

    const bobLatest = await auditor.latest("bob");
    if (bobLatest !== null) {
      return fail("per-userId isolation", "latest('bob') returned non-null when no bob records exist");
    }

    // Alice's record is still accessible.
    const aliceHistory = await auditor.history("alice", 0);
    if (aliceHistory.length !== 1) {
      return fail("per-userId isolation", `alice's history has ${aliceHistory.length} entries, want 1`);
    }

    return pass("per-userId isolation");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-7 — Time monotonicity (caller-clock responsibility).
 *
 * If an observation with an `observedAt` earlier than a previous record is
 * appended, the auditor still accepts it (the caller is responsible for the
 * clock). The record IS appended; `history(userId, 0)` returns 2 entries.
 *
 * This documents the contract: the log is an audit trail of *observations*,
 * not a time-ordered log. The auditor never rejects or reorders records based
 * on timestamps.
 */
async function testTimeMonotonicity(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    // Record at t=5000 first, then t=1000 (going backwards).
    await auditor.record(makeObs("alice", KEY_A, { observedAt: 5_000 }));
    const verdict = await auditor.record(makeObs("alice", KEY_B, { observedAt: 1_000 }));

    if (verdict !== "changed") {
      return fail(
        "time monotonicity",
        `Expected 'changed' verdict; got '${verdict}'`,
      );
    }

    const all = await auditor.history("alice", 0);
    if (all.length !== 2) {
      return fail(
        "time monotonicity",
        `Expected 2 rows after backwards-clock append; got ${all.length}`,
      );
    }

    // Insertion order: KEY_A at index 0, KEY_B at index 1 (most-recent in history = first).
    if (all[0].observedAt !== 1_000) {
      return fail(
        "time monotonicity",
        `most-recent entry has observedAt=${all[0].observedAt}, want 1000 (backwards-clock append)`,
      );
    }

    return pass("time monotonicity");
  } finally {
    await cleanup(auditor, dbName);
  }
}

/**
 * TC-8 — Persistence across open/close/reopen.
 *
 * Records written by one `KeyAuditorIDB` instance are readable by a second
 * instance pointed at the same database name.
 */
async function testPersistence(): Promise<TestCase> {
  const dbName = `kaether-keyaudit-test-${crypto.randomUUID()}`;
  let writer: KeyAuditorIDB | null = null;
  let reader: KeyAuditorIDB | null = null;
  try {
    writer = await KeyAuditorIDB.open(dbName);
    await writer.record(makeObs("alice", KEY_A, { observedAt: 1_000 }));
    await writer.record(makeObs("alice", KEY_B, { observedAt: 2_000 }));
    writer.close();
    writer = null;

    reader = await KeyAuditorIDB.open(dbName);
    const history = await reader.history("alice", 0);

    if (history.length !== 2) {
      return fail("persistence", `reader saw ${history.length} records, want 2`);
    }

    // Insertion order in history() is most-recent first: KEY_B then KEY_A.
    if (history[0].identityPubKey[0] !== 0xbb) {
      return fail(
        "persistence",
        `history[0].identityPubKey[0] = 0x${history[0].identityPubKey[0].toString(16)}, want 0xbb`,
      );
    }
    if (history[1].identityPubKey[0] !== 0xaa) {
      return fail(
        "persistence",
        `history[1].identityPubKey[0] = 0x${history[1].identityPubKey[0].toString(16)}, want 0xaa`,
      );
    }

    return pass("persistence");
  } finally {
    try {
      writer?.close();
      reader?.close();
      await KeyAuditorIDB.deleteDatabase(dbName);
    } catch {
      // swallowed
    }
  }
}

/**
 * TC-9 — compareAgainstLatest is non-mutating.
 *
 * Calling `compareAgainstLatest` multiple times must not change the number of
 * records in the store.
 */
async function testCompareIsNonMutating(): Promise<TestCase> {
  const { auditor, dbName } = await freshDB();
  try {
    await auditor.record(makeObs("alice", KEY_A));

    await auditor.compareAgainstLatest(makeObs("alice", KEY_B));
    await auditor.compareAgainstLatest(makeObs("alice", KEY_B));
    await auditor.compareAgainstLatest(makeObs("alice", KEY_B));

    const all = await auditor.history("alice", 0);
    if (all.length !== 1) {
      return fail(
        "compareAgainstLatest is non-mutating",
        `history has ${all.length} entries after 3 compare calls, want 1`,
      );
    }

    return pass("compareAgainstLatest is non-mutating");
  } finally {
    await cleanup(auditor, dbName);
  }
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Key transparency (key-change audit)",
  isVerified: true,
  run: async () => {
    const cases: TestCase[] = await Promise.all([
      testFirstObservation(),
      testUnchanged(),
      testChanged(),
      testHistoryOrder(),
      testAppendOnly(),
      testPerUserIsolation(),
      testTimeMonotonicity(),
      testPersistence(),
      testCompareIsNonMutating(),
    ]);
    return buildSuiteResult(cases);
  },
});
